import {
	Children,
	isValidElement,
	useCallback,
	useMemo,
	useState,
} from "react";
import { type LayoutChangeEvent, View } from "react-native";
import useBreakpoints from "../useBreakpoints";
import Box from "./Box";

interface Props {
	children: React.ReactNode;
	fullWidth?: boolean;
	gap?: number;
}

type MasonryItem = { index: number; key: string; node: React.ReactNode };
type MasonryColumn = { id: string; items: MasonryItem[] };

const getItemKey = (node: React.ReactNode, index: number): string => {
	if (isValidElement(node) && node.key != null) {
		return node.key;
	}
	return `item-${index}`;
};

const MasonryGrid = ({ children, fullWidth, gap = 16 }: Props) => {
	const { smUp } = useBreakpoints();
	const columns = useMemo(() => {
		if (fullWidth) {
			return 1;
		}
		return smUp ? 2 : 1;
	}, [fullWidth, smUp]);

	const childrenArray = useMemo(() => Children.toArray(children), [children]);

	const [heights, setHeights] = useState<Record<number, number>>({});

	const handleLayout = useCallback((index: number, e: LayoutChangeEvent) => {
		const height = Math.round(e.nativeEvent.layout.height);
		setHeights((prev) => {
			if (prev[index] === height) {
				return prev;
			}
			return { ...prev, [index]: height };
		});
	}, []);

	const columnsData = useMemo<MasonryColumn[]>(() => {
		const cols: MasonryColumn[] = Array.from({ length: columns }, (_, i) => ({
			id: `column-${i}`,
			items: [],
		}));

		// Until every item has been measured, fall back to round-robin so items
		// spread across columns and each gets a chance to report its height.
		const allMeasured =
			columns > 1 &&
			childrenArray.every((_, index) => heights[index] !== undefined);

		if (!allMeasured) {
			childrenArray.forEach((node, index) => {
				cols[index % columns].items.push({
					index,
					key: getItemKey(node, index),
					node,
				});
			});
			return cols;
		}

		// Greedy masonry: place each item into the currently shortest column.
		const columnHeights = new Array<number>(columns).fill(0);
		childrenArray.forEach((node, index) => {
			let shortest = 0;
			for (let i = 1; i < columns; i++) {
				if (columnHeights[i] < columnHeights[shortest]) {
					shortest = i;
				}
			}
			cols[shortest].items.push({ index, key: getItemKey(node, index), node });
			columnHeights[shortest] += heights[index] ?? 0;
		});
		return cols;
	}, [childrenArray, columns, heights]);

	return (
		<Box flexDirection="row" gap={gap} style={{ margin: 0 }}>
			{columnsData.map((column) => (
				<View key={column.id} style={{ flex: 1, gap }}>
					{column.items.map(({ index, key, node }) => (
						<View key={key} onLayout={(e) => handleLayout(index, e)}>
							{node}
						</View>
					))}
				</View>
			))}
		</Box>
	);
};

export default MasonryGrid;
